import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  RepositoryClassificationListItem,
  RepositoryClassificationOption,
  RepositoryClassificationRequest,
} from './repositoryTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check here would drop it and the
    server would read the axis as unmentioned, showing everything instead of nothing.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The information classification catalog — Repository → Settings → Classifications.
 *
 * Seeded with the matrix's four on boot, so a fresh deployment can register immediately.
 * An administrator may still delete all of them, and the register form reads that and
 * withholds rather than offering a required picker with nothing in it.
 */
export const repositoryClassificationsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<RepositoryClassificationListItem>>(
      `/api/repository/settings/classifications${toQueryString(query)}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    api<RepositoryClassificationListItem>(
      `/api/repository/settings/classifications/${id}`,
      { signal },
    ),

  /** Active classifications for the register form's picker. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<RepositoryClassificationOption[]>('/api/repository/classifications', { signal }),

  create: (request: RepositoryClassificationRequest) =>
    api<RepositoryClassificationListItem>('/api/repository/settings/classifications', {
      method: 'POST',
      body: request,
    }),

  update: (id: string, request: RepositoryClassificationRequest) =>
    api<RepositoryClassificationListItem>(`/api/repository/settings/classifications/${id}`, {
      method: 'PUT',
      body: request,
    }),

  /** Soft delete. Documents already classified under it keep resolving its name. */
  deactivate: (id: string) =>
    api<void>(`/api/repository/settings/classifications/${id}`, { method: 'DELETE' }),

  activate: (id: string) =>
    api<void>(`/api/repository/settings/classifications/${id}/restore`, { method: 'POST' }),

  clear: () =>
    api<number>('/api/repository/settings/classifications/clear', { method: 'POST' }),
};
